import { computed, effect, Injectable, Signal, signal, WritableSignal } from '@angular/core';

import { ThemeUtilsService } from '@core/theme-logic';
import { RECRUITEE_THEME_LS_KEY, SupportedThemesT } from '@core/theme-util';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  public readonly isDarkMode: Signal<boolean>;
  public readonly currentTheme: Signal<SupportedThemesT>;

  private theme!: WritableSignal<SupportedThemesT>;
  private hasUserPreference!: boolean;

  constructor() {
    this.theme = signal<SupportedThemesT>(this.getInitialTheme());
    this.hasUserPreference = this.checkHasUserPreference();
    this.isDarkMode = computed(() => this.theme() === 'dark');
    this.currentTheme = this.theme.asReadonly();

    effect(() => {
      const theme = this.theme();
      ThemeUtilsService.setThemeHTMLData(theme);
    });

    ThemeUtilsService.setThemeHTMLData(this.theme());
    this.listenToSystemThemeChanges();
  }

  public toggleTheme(): void {
    this.theme.update(current => (current === 'light' ? 'dark' : 'light'));
    this.persistTheme(this.theme());
    this.hasUserPreference = true;
  }

  private getInitialTheme(): SupportedThemesT {
    const stored = localStorage.getItem(RECRUITEE_THEME_LS_KEY);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
    return this.getSystemTheme();
  }

  private checkHasUserPreference(): boolean {
    const stored = localStorage.getItem(RECRUITEE_THEME_LS_KEY);
    return stored === 'dark' || stored === 'light';
  }

  private getSystemTheme(): SupportedThemesT {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }

  private persistTheme(theme: SupportedThemesT): void {
    localStorage.setItem(RECRUITEE_THEME_LS_KEY, theme);
  }

  private listenToSystemThemeChanges(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', event => {
      if (!this.hasUserPreference) {
        this.theme.set(event.matches ? 'dark' : 'light');
      }
    });
  }
}
